import { takeLatest, call, put, select } from 'redux-saga/effects'
import {
    LOGIN_REQUEST,
    enqueueSnackbar,
    loginSuccess,
    loginError,
    CHECK_ROUTE_REQUEST,
    profileUpdateError,
    profileUpdateSuccess,
    PROFILE_UPDATE_REQUEST,
    profileChangePasswordSuccess,
    profileChangePasswordError,
    PROFILE_CHANGE_PASSWORD_REQUEST,
    logout,
    setIsTermsStep,
    setTempCreds,
    LOGIN_WITH_TS_REQUEST,
    forgotPasswordSuccess,
    forgotPasswordError,
    FORGOT_PASSWORD_REQUEST,
    resetPasswordSuccess,
    resetPasswordError,
    RESET_PASSWORD_REQUEST,
    saveLocaleSuccess,
    saveLocaleError,
    SAVE_LOCALE_REQUEST,
    setLocale,
    startLoading,
    stopLoading,
    getProfileUserSuccess,
    getProfileUserError,
    GET_PROFILE_USER,
    RELOAD_USER,
    GET_HAS_TOM_DEVICES,
    getHasTomDevicesSuccess,
    SET_ORGANISATION_PHONE,
    setOrganisationPhoneNumberSuccess,
    setOrganisationPhoneNumberError,
    getOrganisationDataSuccess,
    GET_ORGANISATION_DATA_REQUEST,
    setTwoFactorToken,
    VERIFY_2FA_REQUEST
} from './actions'
import axios from 'axios'
import {
    decodeJwt,
    detachDelightedSurvey,
    initDelightedSurvey
} from '../../utils'
import { LOCALE_MAP, LOGIN_EX_TYPES, HOTSWAP_PERMISSIONS } from './constants'
import { getSagasMsgs } from './selectors'

export const watchMainSaga = [
    takeLatest(LOGIN_REQUEST, login),
    takeLatest(CHECK_ROUTE_REQUEST, check),
    takeLatest(PROFILE_UPDATE_REQUEST, updateProfile),
    takeLatest(PROFILE_CHANGE_PASSWORD_REQUEST, profileChangePassword),
    takeLatest(LOGIN_WITH_TS_REQUEST, loginWithTs),
    takeLatest(FORGOT_PASSWORD_REQUEST, forgotPassword),
    takeLatest(RESET_PASSWORD_REQUEST, resetPassword),
    takeLatest(SAVE_LOCALE_REQUEST, saveLocale),
    takeLatest(GET_PROFILE_USER, getUser),
    takeLatest(RELOAD_USER, reloadUser),
    takeLatest(GET_HAS_TOM_DEVICES, getHasTomDevices),
    takeLatest(SET_ORGANISATION_PHONE, setOrganisationPhoneNumber),
    takeLatest(GET_ORGANISATION_DATA_REQUEST, getOrganisationData),
    takeLatest(VERIFY_2FA_REQUEST, verifyTwoFactor)
]

function* login(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        const { userBody } = action
        const response = yield call(
            loginUser,
            userBody.email,
            userBody.password
        )
        if (response.data.status === 'TERMS_REQUIRED') {
            yield put(setIsTermsStep(true))
            yield put(
                setTempCreds({
                    email: userBody.email,
                    password: userBody.password
                })
            )
        } else if (response.data.status === 'TWO_FACTOR_REQUIRED') {
            yield put(
                setTwoFactorToken({
                    twoFactorToken: response.data.twoFactorToken
                })
            )
        } else {
            const decoded = decodeJwt(response.data.token)
            try {
                // we're passing token because we want to get this permissions before homepage is rendered (and token added to the state)
                // TODO: check if there's additional login sagas and make sure we're fetching permissions there also
                // TODO: consider storing data in store instead of in localStorage
                const hotSwapPermissions = yield call(
                    getCustomerPermissions,
                    response.data.token
                )
                for (let hotSwapPermission of hotSwapPermissions) {
                    for (let key of Object.values(HOTSWAP_PERMISSIONS)) {
                        if (hotSwapPermission[key] && !localStorage[key]) {
                            localStorage.setItem(key, true)
                        }
                    }
                }
            } catch (error) {
                yield put(
                    enqueueSnackbar({
                        message: sagasMsgs.loginWithTsEx,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            }
            yield put(
                loginSuccess(
                    response.data.token,
                    decoded.iss.user,
                    decoded.permissions
                )
            )
            initDelightedSurvey(
                decoded.iss.user.email,
                decoded.iss.user.name,
                decoded.iss.user.original_customer_name
            )
            yield put(setLocale(LOCALE_MAP[decoded.iss.user.language]))
        }
    } catch (ex) {
        yield put(loginError())
        if (ex?.response?.data?.type === LOGIN_EX_TYPES.wrong_login_data) {
            yield put(
                enqueueSnackbar({
                    message: sagasMsgs.loginEx,
                    options: {
                        variant: 'error'
                    }
                })
            )
        } else {
            yield put(
                enqueueSnackbar({
                    message: sagasMsgs.loginWithTsEx,
                    options: {
                        variant: 'error'
                    }
                })
            )
        }
    }
}

async function getCustomerPermissions(token) {
    const response = await axios.get(
        `${process.env.API_URL}/customers/permissions`,
        {
            headers: {
                Authorization: token
            }
        }
    )
    return response.data
}

async function loginUser(email, password) {
    const response = await axios.post(`${process.env.API_URL}/users/v2/auth`, {
        email: email,
        password: password
    })
    return response
}

function* loginWithTs(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        const { userBody } = action
        const response = yield call(
            loginUserWithTs,
            userBody.email,
            userBody.password
        )
        if (response.data.status === 'TWO_FACTOR_REQUIRED') {
            yield put(
                setTwoFactorToken({
                    twoFactorToken: response.data.twoFactorToken
                })
            )
            yield put(setIsTermsStep(false))
            return
        }
        const decoded = decodeJwt(response.data.token)
        yield put(
            loginSuccess(
                response.data.token,
                decoded.iss.user,
                decoded.permissions
            )
        )
        initDelightedSurvey(
            decoded.iss.user.email,
            decoded.iss.user.name,
            decoded.iss.user.original_customer_name
        )
        yield put(setLocale(LOCALE_MAP[decoded.iss.user.language]))
    } catch (ex) {
        yield put(loginError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.loginWithTsEx,
                options: {
                    variant: 'error'
                }
            })
        )
    }
}

async function loginUserWithTs(email, password) {
    const response = await axios.post(
        `${process.env.API_URL}/users/v2/auth-with-ts`,
        {
            email: email,
            password: password
        }
    )
    return response
}

function* check(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        const { userId } = action
        yield call(checkRoute, userId)
    } catch (ex) {
        yield put(logout())
        detachDelightedSurvey()
        action.history.push('/')
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.sessionEx,
                options: {
                    variant: 'error'
                }
            })
        )
    }
}

async function checkRoute(userId) {
    const response = await axios.get(
        `${process.env.API_URL}/users/check/${userId}`
    )
    return response
}

function* updateProfile(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield call(updateProfileRequest, action.body)
        yield put(profileUpdateSuccess(action.body))
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.updataProfileRes,
                options: {
                    variant: 'success'
                }
            })
        )
    } catch (ex) {
        yield put(profileUpdateError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.updataProfileEx,
                options: {
                    variant: 'error'
                }
            })
        )
    }
}

async function updateProfileRequest(body) {
    const response = await axios.patch(`${process.env.API_URL}/profile`, body)
    return response
}

function* profileChangePassword(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield call(profileChangePasswordRequest, action.body)
        yield put(profileChangePasswordSuccess())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.pswdChangeRes,
                options: {
                    variant: 'success'
                }
            })
        )
    } catch (ex) {
        yield put(profileChangePasswordError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.pswdChangeEx,
                options: {
                    variant: 'error'
                }
            })
        )
    }
}

async function profileChangePasswordRequest(body) {
    await axios.patch(`${process.env.API_URL}/profile/change-password`, body)
}

function* forgotPassword(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield call(forgotPasswordRequest, action.email)
        yield put(forgotPasswordSuccess())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.forgotPswdRes,
                options: {
                    variant: 'success'
                }
            })
        )
    } catch (ex) {
        yield put(forgotPasswordError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.forgotPswdEx,
                options: {
                    variant: 'error'
                }
            })
        )
    }
}

async function forgotPasswordRequest(email) {
    await axios.post(`${process.env.API_URL}/users/reset-password-request`, {
        email: email
    })
}

function* resetPassword(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield call(resetPasswordRequest, action.token, action.password)
        yield put(resetPasswordSuccess())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.resetPswdRes,
                options: {
                    variant: 'success'
                }
            })
        )
    } catch (ex) {
        yield put(resetPasswordError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.resetPswdEx,
                options: {
                    variant: 'error'
                }
            })
        )
    }
}

async function resetPasswordRequest(token, password) {
    await axios.post(`${process.env.API_URL}/users/reset-password`, {
        token,
        password
    })
}

function* saveLocale(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield call(saveLocaleRequest, action.localeCode)
        yield put(saveLocaleSuccess())
    } catch (ex) {
        yield put(saveLocaleError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.saveLocaleEx,
                options: {
                    variant: 'error'
                }
            })
        )
    }
}

async function saveLocaleRequest(localeCode) {
    await axios.patch(`${process.env.API_URL}/profile/locale`, {
        localeCode
    })
}

function* getUser(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield put(startLoading())
        const response = yield call(getUserRequest, action.userId)
        yield put(getProfileUserSuccess(response))
        yield put(stopLoading())
    } catch (ex) {
        yield put(getProfileUserError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.getUserEx,
                options: {
                    variant: 'error'
                }
            })
        )
        yield put(stopLoading())
    }
}

async function getUserRequest(userId) {
    const response = await axios.get(`${process.env.API_URL}/users/${userId}`)
    return response.data
}

function* reloadUser(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield put(startLoading())

        let response

        if (action.payload.adminReload) {
            response = yield call(reloadAdminUserRequest)
        } else {
            response = yield call(reloadUserRequest)
        }

        const decoded = decodeJwt(response.data)
        yield put(
            loginSuccess(response.data, decoded.iss.user, decoded.permissions)
        )
        yield put(setLocale(LOCALE_MAP[decoded.iss.user.language]))
        const profileUser = yield call(getUserRequest, decoded.iss.user.id)
        yield put(getProfileUserSuccess(profileUser))
        yield put(stopLoading())
        if (action.payload.adminReload) window.location.reload(true)
    } catch (ex) {
        yield put(loginError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.loginWithTsEx,
                options: {
                    variant: 'error'
                }
            })
        )
        yield put(stopLoading())
    }
}

async function reloadAdminUserRequest() {
    const response = await axios.post(
        `${process.env.API_URL}/users/reload-admin`
    )
    return response
}

async function reloadUserRequest() {
    const response = await axios.post(
        `${process.env.API_URL}/users/reload-user`
    )
    return response
}

function* getHasTomDevices() {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        const response = yield call(getHasTomDevicesRequest)
        let hasTomDevices = false
        try {
            const tomDevicesAmount = Number(response.data)
            hasTomDevices = tomDevicesAmount > 0
        } catch (ex) {
            // do nothing basically
            hasTomDevices = false
        }
        yield put(getHasTomDevicesSuccess(hasTomDevices))
    } catch (ex) {
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.getTomPicturesEx,
                options: {
                    variant: 'error'
                }
            })
        )
    }
}

async function getHasTomDevicesRequest() {
    const response = await axios.get(`${process.env.API_URL}/devices/has-tom`)
    return response
}

function* setOrganisationPhoneNumber(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        const response = yield call(setOrganisationPhoneRequest, action.data)
        yield put(setOrganisationPhoneNumberSuccess(response))
    } catch (ex) {
        yield put(setOrganisationPhoneNumberError())
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.organisationPhoneEx,
                options: {
                    variant: 'error'
                }
            })
        )
    }
}

async function setOrganisationPhoneRequest(data) {
    const response = await axios.patch(
        `${process.env.API_URL}/profile/customers/${data.customerId}`,
        {
            organisation_phone_number: data.organisation_phone_number
        }
    )
    return response
}

function* getOrganisationData(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        const response = yield call(
            getOrganisationDataRequest,
            action.customerId
        )
        yield put(getOrganisationDataSuccess(response.data))
    } catch (ex) {
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.commonLoadingError,
                options: {
                    variant: 'error'
                }
            })
        )
    }
}

async function getOrganisationDataRequest(customerId) {
    const response = await axios.get(
        `${process.env.API_URL}/profile/customers/${customerId}`
    )
    return response
}

function* verifyTwoFactor(action) {
    const sagasMsgs = yield select(getSagasMsgs)
    try {
        yield put(startLoading())
        const { twoFactorToken, code } = action
        const response = yield call(
            verifyTwoFactorRequest,
            twoFactorToken,
            code
        )
        const decoded = decodeJwt(response.data.token)
        try {
            const hotSwapPermissions = yield call(
                getCustomerPermissions,
                response.data.token
            )
            for (let hotSwapPermission of hotSwapPermissions) {
                for (let key of Object.values(HOTSWAP_PERMISSIONS)) {
                    if (hotSwapPermission[key] && !localStorage[key]) {
                        localStorage.setItem(key, true)
                    }
                }
            }
        } catch (error) {
            yield put(
                enqueueSnackbar({
                    message: sagasMsgs.loginWithTsEx,
                    options: {
                        variant: 'error'
                    }
                })
            )
        }
        yield put(
            loginSuccess(
                response.data.token,
                decoded.iss.user,
                decoded.permissions
            )
        )
        initDelightedSurvey(
            decoded.iss.user.email,
            decoded.iss.user.name,
            decoded.iss.user.original_customer_name
        )
        yield put(setLocale(LOCALE_MAP[decoded.iss.user.language]))
        yield put(stopLoading())
    } catch (ex) {
        yield put(
            enqueueSnackbar({
                message: sagasMsgs.verify2faEx,
                options: {
                    variant: 'error'
                }
            })
        )
        yield put(stopLoading())
    }
}

async function verifyTwoFactorRequest(twoFactorToken, code) {
    const response = await axios.post(
        `${process.env.API_URL}/users/v2/auth/verify-2fa`,
        {
            twoFactorToken,
            code
        }
    )
    return response
}
